/**
 * Money is integer cents. Never a float.
 *
 * The prototype summed `parseFloat` values straight from the database, which is
 * correct to the cent only by luck: 0.1 + 0.2 is famously not 0.3. Every figure
 * in the totals ladder is money, and the ladder is the product
 * (docs/adr/0011-the-totals-ladder.md), so it runs on integers end to end and
 * converts back to a decimal string only for display.
 */

/** An amount of money, in whole cents. Always an integer. */
export type Cents = number & { readonly __brand: "Cents" };

/** A percentage rate, e.g. 17 for 17%, 9.5 for 9.5%. Not money. */
export type Percent = number & { readonly __brand: "Percent" };

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Parse anything the database or a text input can hand us into cents.
 *
 * Accepts numbers, numeric strings, currency-formatted strings ("1,089.81",
 * "$1,089.81"), null and undefined. Anything unparseable is 0 — the prototype
 * behaved the same way (`num()`), and a blank input must not poison a total.
 */
export function toCents(value: unknown): Cents {
  if (value == null) return 0 as Cents;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0 as Cents;
    return Math.round(value * 100) as Cents;
  }
  const cleaned = String(value).replace(/[$,\s]/g, "");
  if (cleaned === "") return 0 as Cents;
  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed)) return 0 as Cents;
  return Math.round(parsed * 100) as Cents;
}

/** Build cents from a whole-cent integer that is already known to be exact. */
export function cents(value: number): Cents {
  return Math.round(value) as Cents;
}

/** Convert cents back to a decimal number, for writing to a numeric column. */
export function toDecimal(value: Cents): number {
  return value / 100;
}

/** Parse a percentage rate. Same lenient rules as {@link toCents}. */
export function toPercent(value: unknown): Percent {
  if (value == null) return 0 as Percent;
  if (isFiniteNumber(value)) return value as Percent;
  const parsed = Number.parseFloat(String(value).replace(/[%,\s]/g, ""));
  return (Number.isFinite(parsed) ? parsed : 0) as Percent;
}

export const addCents = (...values: Cents[]): Cents =>
  values.reduce<number>((sum, v) => sum + v, 0) as Cents;

export const subCents = (a: Cents, b: Cents): Cents => (a - b) as Cents;

export const sumBy = <T>(rows: readonly T[], pick: (row: T) => Cents): Cents =>
  rows.reduce<number>((sum, row) => sum + pick(row), 0) as Cents;

/**
 * Multiply money by a quantity. Quantities are genuinely fractional (2.5 SQ),
 * so this rounds half-up at the cent, matching how the database's generated
 * `amount` column and the source system both behave.
 */
export function multiplyCents(amount: Cents, quantity: number): Cents {
  if (!Number.isFinite(quantity)) return 0 as Cents;
  return Math.round(amount * quantity) as Cents;
}

/** Apply a percentage rate to an amount, e.g. tax or overhead. */
export function percentOf(amount: Cents, rate: Percent): Cents {
  if (!Number.isFinite(rate)) return 0 as Cents;
  return Math.round((amount * rate) / 100) as Cents;
}

/**
 * Divide an amount between parts in proportion to their weights, to the cent.
 *
 * The shares always add up to `total` exactly and none is negative: each part
 * takes the whole cents of its share, and the cents left over go one at a time
 * to the parts whose shares were cut the most. A weight that is negative or
 * not a number counts as nothing; when every weight is nothing the amount is
 * divided evenly. Used to spread one estimate's price across its lines
 * (ADR 0150), which must add up to the price the ladder solved for.
 */
export function splitCents(total: Cents, weights: readonly number[]): Cents[] {
  if (weights.length === 0) return [];
  const amount = Math.max(0, Math.round(total));
  const clean = weights.map((w) => (Number.isFinite(w) && w > 0 ? w : 0));
  const sum = clean.reduce((s, w) => s + w, 0);
  const exact = clean.map((w) =>
    sum > 0 ? (amount * w) / sum : amount / clean.length,
  );
  const shares = exact.map((x) => Math.floor(x));
  let left = amount - shares.reduce((s, n) => s + n, 0);
  const order = exact
    .map((x, at) => ({ at, cut: x - Math.floor(x) }))
    .sort((a, b) => b.cut - a.cut || a.at - b.at);
  for (let i = 0; left > 0; i = (i + 1) % order.length, left -= 1) {
    shares[order[i]!.at]! += 1;
  }
  return shares as Cents[];
}

/**
 * A ratio for display as a percentage, e.g. gross profit over grand total.
 * Returns 0 when the denominator is 0 — a job with no contract has no margin,
 * and NaN must never reach the screen.
 */
export function ratio(numerator: Cents, denominator: Cents): number {
  if (denominator === 0) return 0;
  return numerator / denominator;
}

/**
 * A per-unit rate in dollars, held to five decimal places.
 *
 * Deliberately not {@link Cents}: `estimate_items.unit_price` is finer than a
 * cent — a standing-seam clip is priced at $0.12345 and sold eight hundred at a
 * time, so rounding the rate to the cent before multiplying moves the total by
 * dollars. The rate is the one money-adjacent number that is not integer
 * cents, and these three helpers are the only arithmetic allowed to touch it.
 */
export type UnitRate = number & { readonly __brand: "UnitRate" };

const RATE_SCALE = 100_000;

/** Parse a rate the way {@link toCents} parses money: leniently, blank is 0. */
export function toUnitRate(value: unknown): UnitRate {
  if (value == null) return 0 as UnitRate;
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseFloat(String(value).replace(/[$,\s]/g, ""));
  if (!Number.isFinite(parsed)) return 0 as UnitRate;
  return (Math.round(parsed * RATE_SCALE) / RATE_SCALE) as UnitRate;
}

/** The line total a rate works out to: rate × quantity, rounded at the cent. */
export function rateTimesQty(rate: UnitRate, qty: number): Cents {
  if (!Number.isFinite(qty)) return 0 as Cents;
  return Math.round(rate * qty * 100) as Cents;
}

/**
 * The rate a sold-at number implies: total ÷ quantity, at five decimals.
 *
 * A job is usually sold at a number rather than a rate, and then the rate
 * follows from it. No quantity means no rate — 0, the same blank-is-0 stance
 * as {@link toCents}, never Infinity into a numeric column.
 */
export function rateFromTotal(total: Cents, qty: number): UnitRate {
  if (!Number.isFinite(qty) || qty <= 0) return 0 as UnitRate;
  return (Math.round((total * (RATE_SCALE / 100)) / qty) /
    RATE_SCALE) as UnitRate;
}

/** Format cents as "1,089.81". No currency symbol — the UI supplies it. */
/**
 * Money for a person to read: "$1,234.56", and "-$100.00" for a negative.
 *
 * `formatCents` deliberately leaves the symbol off — it is the number, and
 * somewhere like a CSV or a form field wants it bare. This is the display form,
 * and the sign goes outside the symbol because "$-100.00" is not how anybody
 * writes it.
 *
 * `locale` is for text that leaves the building. A screen follows whoever is
 * reading it, so the default is the browser's; a letter is written once on a
 * server and read by a customer in the US, so it pins "en-US" — otherwise the
 * same letter prints "$18.450,00" from a laptop set to German and "$18,450.00"
 * from the Edge Function (ADR 0122).
 */
export function formatMoney(value: Cents, locale?: string): string {
  const formatted = formatCents(Math.abs(value) as Cents, locale);
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

export function formatCents(value: Cents, locale?: string): string {
  return (value / 100).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a ratio as a whole percentage, e.g. 0.5183 -> "52%".
 *
 * The source system showed 51% in one place and 52% in another for this very
 * number; ADR 0011 says ours rounds consistently.
 */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value * 100)}%`;
}
